import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog_v2";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select_v2";
import { ROLE_CONFIG } from "./credit-data";
import { Coins, User, Users } from "lucide-react";
import localization from "services/localization";
import { useThemeStore } from "../../../context/useThemeStore";
import { useUserStore } from "context/useUserStore";
import { toast } from "react-toastify";

export function AllocateCreditsModal({
  open,
  onClose,
  users,
  remainingOrgCredits,
  onAllocateToUser,
  onAllocateToRole,
}) {
  const { userData, appUser } = useUserStore();
  const [allocationType, setAllocationType] = useState("user");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const { theme } = useThemeStore();

  const handleSubmit = () => {
    var wallet = users.find((u) => u.id === selectedUserId)?.wallet;
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      setError(localization.enter_valid_positive_number || "Please enter a valid positive number");
      return;
    }
    if (numAmount > remainingOrgCredits) {
      toast.error(localization.insufficient_credits || "Insufficient credits");
      setError(
        (localization.only_available_credits || "Only {credits} credits available").replace(
          "{credits}",
          remainingOrgCredits.toLocaleString(),
        ),
      );
      return;
    }

    if (allocationType === "user") {
      if (!selectedUserId) {
        setError(localization.select_user || "Please select a user");
        return;
      }
      onAllocateToUser(wallet, selectedUserId, numAmount);
    } else {
      if (!selectedRole) {
        setError(localization.select_role || "Please select a role");
        return;
      }
      onAllocateToRole(wallet, selectedRole, numAmount);
    }
    handleClose();
  };

  const handleClose = () => {
    setAllocationType("user");
    setSelectedUserId("");
    setSelectedRole("");
    setAmount("");
    setError("");
    onClose();
  };

  const roleUsers = selectedRole
    ? users.filter((u) => u.role === selectedRole && u?.school_id?.includes(userData?.selected_school_id))
    : [];
  const totalForRole = roleUsers.length * Number(amount || 0);

  const select_bg = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "bg-white text-foreground";
  const select_bg2 = theme === "dark" ? " text-white" : "text-foreground";

  return (
    <Dialog open={open} onOpenChange={handleClose} className={select_bg}>
      <DialogContent className={"sm:max-w-md " + select_bg}>
        <DialogHeader>
          <DialogTitle className={"flex items-center gap-2 " + select_bg2}>
            <Coins className="h-5 w-5 text-amber-500" />
            {localization.allocate_credits || "Allocate Credits"}
          </DialogTitle>
          <DialogDescription className={select_bg2}>
            {localization.distribute_credits_text || "Distribute credits to individual users or by role."}
          </DialogDescription>
          <DialogDescription className={select_bg2}>
            {localization.available || "Available"}:{" "}
            <span className="font-semibold text-foreground">{remainingOrgCredits.toLocaleString()}</span>{" "}
            {localization.credits || "credits"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* Allocation Type Toggle */}
          <div className="grid gap-2">
            <Label>{localization.allocation_type || "Allocation Type"}</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={allocationType === "user" ? "default" : "outline"}
                className="flex-1 gap-2"
                onClick={() => {
                  setAllocationType("user");
                  setError("");
                }}
              >
                <User className="h-4 w-4" />
                {localization.individual_user || "Individual User"}
              </Button>
              <Button
                type="button"
                variant={allocationType === "role" ? "default" : "outline"}
                className="flex-1 gap-2"
                onClick={() => {
                  setAllocationType("role");
                  setError("");
                }}
              >
                <Users className="h-4 w-4" />
                {localization.by_role || "By Role"}
              </Button>
            </div>
          </div>

          {/* User or Role Selection */}
          {allocationType === "user" ? (
            <div className="grid gap-2">
              <Label htmlFor="user-select">{localization.select_user || "Select User"}</Label>
              <Select
                value={selectedUserId}
                onValueChange={(v) => {
                  setSelectedUserId(v);
                  setError("");
                }}
              >
                <SelectTrigger id="user-select">
                  <SelectValue placeholder={localization.choose_user_placeholder || "Choose a user..."} />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((u) => u?.school_id?.includes(userData?.selected_school_id))
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <span className="flex items-center gap-2">
                          {user.name}
                          <span className="text-muted-foreground text-xs">({ROLE_CONFIG[user.role]?.label})</span>
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="role-select">{localization.select_role || "Select Role"}</Label>
              <Select
                value={selectedRole}
                onValueChange={(v) => {
                  setSelectedRole(v);
                  setError("");
                }}
              >
                <SelectTrigger id="role-select">
                  <SelectValue placeholder={localization.choose_role_placeholder || "Choose a role..."} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label} ({users.filter((u) => u.role === key).length} {localization.users || "users"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRole && Number(amount) > 0 && (
                <p className="text-xs text-muted-foreground">
                  {(localization.each_role_receive || "Each of the {count} {role}(s) will receive {amount} credits.")
                    .replace("{count}", roleUsers.length)
                    .replace("{role}", ROLE_CONFIG[selectedRole]?.label)
                    .replace("{amount}", Number(amount).toLocaleString())}
                  {` Total: `}
                  <span className="font-semibold text-foreground">{totalForRole.toLocaleString()}</span>{" "}
                  {localization.credits || "credits"}.
                </p>
              )}
            </div>
          )}

          {/* Amount Input */}
          <div className="grid gap-2">
            <Label htmlFor="credit-amount">
              {allocationType === "role"
                ? localization.credits_per_user || "Credits Per User"
                : localization.credits_amount || "Credits Amount"}
            </Label>
            <Input
              id="credit-amount"
              className={select_bg}
              type="number"
              min={1}
              placeholder="e.g. 500"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError("");
              }}
            />
          </div>

          {error && <p className="text-sm text-destructive font-medium">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {localization.cancel || "Cancel"}
          </Button>
          <Button onClick={handleSubmit}>{localization.allocate_credits || "Allocate Credits"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
